// The app session (docs/08-app-design.md sections 5.3 and 7): the only place
// that creates the worker, hands it the sealed master to open, and wipes it.
// The main thread never sees the master, a tree secret or the fee key; it
// holds a WorkerBackend the signer talks to and a worker-backed fee signer.
// Locking closes the engine (which wipes the trees), wipes and terminates
// the worker and so drops the gas tank's key with it, so nothing signable
// survives a lock; the auto-lock timer runs here rather than in any screen.
// While unlocked, `engine` is the one object screens use to read and write
// the chain (engine.ts).
//
// Unlock (docs/18-one-fee-reserve.md section 3) opens the master in the
// worker and re-derives the vault's fee reserve there (`activateFeePayer(0)`);
// no fee key is stored and the legacy relayer envelope is not read. It
// checks the browser can hold the fee key (the capability probe), that the
// stored vault address is the one this build's program id gives for the
// stored genesis key (a setup made under a different program id is
// refused; a different network is 2.5's check, at the first fee use), and
// keeps the stored fee address in step with the derived one through the
// compare-and-set writer (storage.ts `updateFeeFields`, R84). It still
// makes no network read. A stored address that was out of date is named
// once, through the session-only `notice`.
//
// A database a later build has upgraded (the version fence, docs/18
// section 3, writer step 6) is never read as an empty device: the start,
// a lock's re-read and that build's upgrade of an open tab publish
// 'newer-build', which offers neither Create nor Restore.
//
// Two session-only facts live here and nowhere in storage: the development
// switch that skips the program-hash check (R42: it resets at lock and at
// reload, and the frame shows it while it is on) and the runtime itself.
// "Forget this device" goes through `forget()`, which locks first so this
// tab's own database connections are closed before the delete (R47).
//
// The lifecycle (R68): leaving 'unlocked' is published FIRST, then the
// runtime is invalidated and torn down, and a new runtime is installed only
// once that teardown has ended.
// - I1: while the snapshot says 'unlocked', the engine, backend, client and
//   worker all exist. Every screen that reads `engine()` during render sits
//   under App.tsx's RequireUnlocked, which reads the same snapshot, so a
//   lock unmounts it before any queued page update renders.
// - I2: at most one session runtime exists, live or tearing down: `unlock`
//   and `adoptUnlocked` wait for every lock run and teardown in flight
//   (`settled`), and an adoption that finds a runtime another adoption has
//   installed meanwhile (two unlocks in flight: the door remounted by Back
//   or Forward while the first was opening) wipes and ends its own worker
//   rather than replace that runtime. A worker an unlock opened is ended on
//   every failure after the unseal, the ledger open included. Onboarding
//   workers (Create/Restore `freshWorker`) and an unlock's worker before
//   its adoption are not session runtimes and are not held back by this.
// - I3: a lock or forget that starts while an unlock or adoption is in
//   flight wins: the adoption wipes and ends its worker and rejects with
//   "locked" instead of publishing 'unlocked' (the `generation` token).
// - I4: only the session's teardown terminates a worker the session has
//   adopted (`discardWorker`).
// Development builds also check I1: publishing 'unlocked' without an engine
// is refused, and dropping a live runtime while the snapshot still says
// 'unlocked' is reported as a page error once the teardown has started.
// These are assertions compiled away in production (`import.meta.env.DEV`),
// not test hooks.
//
// The legacy escape hatch (docs/18 2.4, "The escape hatch's lifetime", R85): on
// a browser that fails the fee-key probe, a device holding an older relayer
// envelope can still send that reserve's SOL out (legacy-send-out.ts). The
// session owns that operation in one slot, `legacyOp` (at most one per tab,
// begun by `beginLegacySendOut`). `invalidate(reason)` is the only place
// `generation` changes, and it cancels the operation synchronously, so every
// lock, auto-lock, Forget and version change ends it before the session's next
// await; an unlock, `adopt` and `pagehide` cancel it directly. While it is live
// and the door is up ('locked'), the idle and hidden-tab timers run as they do
// while unlocked; its cancel stops them, so none fires over a later unlock.
//
// A sweep's outcome (docs/18 section 6, "After the sweep") is kept as the
// one-session notice as soon as the engine reports it, which survives lock and
// unlock until it is dismissed or the device is forgotten: the door shows it
// while locked and Home after the next unlock. The page that pressed Move takes
// it back (`takeSweepOutcome`) when it is still mounted to show the outcome
// itself, so only an outcome that page could not show (a lock during the
// confirmation, or the page left meanwhile) remains.
//
// The one-sweep latch (docs/18 section 6, one sweep at a time) lives here too,
// not in an engine: `pendingSweep` is the sweep sent and not yet known to have
// landed, failed on chain or expired. Every engine reads and writes it through
// its deps; the engine sets it at the first send attempt and clears it once the
// outcome is known, and an engine of a later unlock that finds it set resumes
// the watch on that signature and refuses a second move meanwhile. It is session
// memory, per tab and per page load: a reload, or a second tab, inside the first
// transfer's window does not see it and can send a second move. A Form A
// duplicate cannot land (the first move emptied the key); a Form B duplicate
// fails on chain and costs at most its 20,000-lamport fee (the probe's figure).
// The older reserve's balance then shows what happened. Forget clears it.

import { address } from '@solana/kit';
import { IndexedDbLedgerStore, MasterStorageError, type SealedMaster, WorkerBackend, WorkerClient, WorkerClientError, openJupiterKey, vaultAddress, workerFeeSigner } from '@thevault/sdk';
import { useSyncExternalStore } from 'react';
import { PROGRAM_ID, PROGRAM_ID_B58 } from './deployment';
import { Engine, type PendingSweep, type SweepOutcome, type VaultInfo, bytesOf } from './engine';
import { type LegacyCancelReason, LegacySendOut, type LegacySendOutHandle } from './legacy-send-out';
import { type RelayerSigner, Rpc, toBase58 } from './rpc';
import { type AppSettings, DEFAULT_RPC, DEFAULT_SETTINGS, LEGACY_DEFAULT_RPC, PUBLICNODE_RPC, forgetDevice, isVersionError, loadSealedMaster, loadSealedRelayer, loadSettings, loadVaultInfo, onAppDbVersionChange, outstandingForget, saveSettings, updateFeeFields } from './storage';

// The program id comes from the deployment entry this build selected (docs/18 2.5); re-exported so existing imports keep working.
export { PROGRAM_ID, PROGRAM_ID_B58 } from './deployment';

/**
 * 'newer-build': a later build has upgraded this device's database past this build's version (the fence, docs/18
 * section 3, writer step 6). This build can neither tell whether a vault is stored nor save one, so no Create or
 * Restore is offered; App.tsx routes the state to Welcome, which shows `NEWER_BUILD_SENTENCE`.
 */
export type SessionState = 'starting' | 'no-vault' | 'locked' | 'unlocked' | 'newer-build';

export const NEWER_BUILD_SENTENCE = 'This device was set up by a newer version of the app. Open it with that version.';

/**
 * A sweep's outcome as the engine reported it (docs/18 section 6, "After the sweep"; engine.ts `SweepOutcome`):
 * `landed` false with a `detail` when it landed and failed (`failed`), or its outcome is not known. `afterLock`: the
 * session had left the runtime that sent it by then. Kept in session memory only, through lock and unlock, until the
 * page that pressed Move takes it, or it is dismissed or forgotten.
 */
export type SweepOutcomeNotice = { kind: 'sweep-outcome'; signature: string; amount: bigint; form: 'A' | 'B'; landed: boolean; failed: boolean; detail: string | null; afterLock: boolean };

/**
 * A one-session notice. 'fee-wallet-rewritten' (docs/18 section 3, unlock step 5): the stored fee reserve address
 * differed from the derived one and was rewritten; Home names `previous` and its balance once. 'fee-wallet-unreadable'
 * (step 5, R92): the stored value is of a kind the writer cannot compare (storage.ts `compareStoredValue`), so it was
 * left as it is and the derived reserve is in use; Home says so once. 'sweep-outcome': as `SweepOutcomeNotice`. Kept
 * here, never in storage.
 */
export type SessionNotice = { kind: 'fee-wallet-rewritten'; previous: string } | { kind: 'fee-wallet-unreadable' } | SweepOutcomeNotice;

export const FEE_KEY_UNSUPPORTED_SENTENCE = 'This browser cannot hold the fee key. Use a current Chrome, Firefox or Safari.';

/** docs/18 2.4, "Browser support": the worker's capability probe failed. Unlock refuses and nothing changes in storage; the 24 words on a supported browser derive the same fee reserve. `detail` is the worker's own message. */
export class FeeKeyUnsupported extends Error {
  constructor(readonly detail?: string) {
    super(FEE_KEY_UNSUPPORTED_SENTENCE);
    this.name = 'FeeKeyUnsupported';
  }
}

/** docs/18 section 3, unlock step 4: the stored vault address is not `vaultAddress(PROGRAM_ID, genesisKey)`, so the setup was made by a build of another deployment (they can share one localhost origin). */
export class WrongDeployment extends Error {
  constructor() {
    super('This device was set up for another deployment of the vault program. Open it with that build.');
    this.name = 'WrongDeployment';
  }
}

/** docs/18 section 3, unlock step 5: the compare-and-set writer found another setup, or a row changed since this unlock read it that does not already hold the derived address (R84). Nothing was written or adopted. */
export class SetupChanged extends Error {
  constructor() {
    super("This device's setup changed while it was being unlocked. Unlock again.");
    this.name = 'SetupChanged';
  }
}

/** What `openSetup` resolves to: the worker-backed signer, the row to adopt, the notice, and the row's stored `feeWallet` as the writer compares it. */
interface OpenedSetup {
  relayer: RelayerSigner;
  info: VaultInfo;
  notice: SessionNotice | null;
  /** The `feeWallet` the row holds after step 5, for the compare-and-set of a pre-R48 address (`legacyAddressWriter`): the derived address once written, or a value the writer cannot compare, left as read (R92). */
  rowFeeWallet: unknown;
}

/**
 * A stored row as the snapshot shows it (the door, the Forget sheet, the chip): `feeWallet` and `relayer` are display
 * data and unauthenticated (docs/18 section 3), so anything but a string shows as no recorded address. The row as
 * stored stays the writer's business: unlock compares what it read (`openSetup`).
 */
function displayInfo(row: VaultInfo): VaultInfo {
  if (typeof row.feeWallet === 'string' && (row.relayer === undefined || typeof row.relayer === 'string')) return row;
  const { feeWallet, relayer, ...rest } = row;
  const shown: VaultInfo = rest;
  if (typeof feeWallet === 'string') shown.feeWallet = feeWallet;
  if (typeof relayer === 'string') shown.relayer = relayer;
  return shown;
}

export interface SessionSnapshot {
  state: SessionState;
  settings: AppSettings;
  /** Milliseconds until the auto-lock fires, for the lock control's tooltip; null when locked. */
  autoLockAt: number | null;
  /** The vault this device is set up for, once Create or Restore has run; public data. */
  info: VaultInfo | null;
  /** Section 7: this session signs even against a program whose hash differs from the published one. Never stored; false again after lock (R42). */
  development: boolean;
  /**
   * "Forget this device" in flight or blocked (R53): kept here, not in the
   * page that started it, because forgetting locks first and locking
   * unmounts that page. `blocked` carries the sentence to show, with a
   * retry, on whatever screen is up next; null once the deletion is done
   * or nothing is in progress.
   */
  forget: { status: 'pending' } | { status: 'blocked'; message: string } | null;
  /**
   * A one-session notice. Set by unlock (docs/18 section 3, step 5) and cleared by lock, forget, the next unlock and
   * `dismissNotice()`; or a sweep's outcome (section 6), cleared only by `takeSweepOutcome()`, `dismissNotice()` and
   * forget.
   */
  notice: SessionNotice | null;
}

type Listener = () => void;

/** `lock()`'s options. `auto`: the idle or hidden-tab timer fired; for a legacy send-out it is the auto-lock of docs/18 2.4 step 3. */
interface LockOptions {
  forgotten?: boolean;
  newerBuild?: boolean;
  auto?: boolean;
}

const FORGETTING = 'This device is being forgotten; its data is queued for deletion and cannot be opened.';

/** Wipe a worker's keys and end it. A worker that answered its last request answers the wipe too; a failed wipe still terminates. */
async function endWorker(client: WorkerClient, worker: Worker): Promise<void> {
  try {
    await client.wipe();
  } catch {
    /* terminated below regardless */
  }
  worker.terminate();
}

/**
 * Storage reads for `start` and a lock's re-read. A failed read counts as absent, as before, except the version
 * fence's VersionError (docs/18 section 3, writer step 6): every open of a database a later build has upgraded fails
 * with it, and reading that as absent would offer Create and Restore over a stored vault. `fenced()` says one was seen.
 */
function fencedReads(): { read: <T>(p: Promise<T>, fallback: T) => Promise<T>; fenced: () => boolean } {
  let seen = false;
  return {
    read: (p, fallback) =>
      p.catch((e: unknown) => {
        if (isVersionError(e)) seen = true;
        return fallback;
      }),
    fenced: () => seen,
  };
}

/**
 * True when an `unlock` rejection means the passphrase did not open this device's sealed master: the AES-GCM
 * authentication failed (a wrong passphrase, or an envelope altered since it was sealed; the two cannot be told apart).
 * Nothing else is: a storage read, the worker, the capability probe (`FeeKeyUnsupported`), the deployment check
 * (`WrongDeployment`), the fee writer's refusal (`SetupChanged`), the ledger open, or an unlock abandoned for a lock
 * or forget (I3) says nothing about the passphrase, and the door must not blame it (DeviceDoor.tsx). Classifies the
 * error `unlock` already throws. The master is opened inside the worker, whose error crosses as a WorkerClientError
 * carrying only the message of the SDK's MasterStorageError 'BadPassphraseOrTampered' (master-storage.ts openBytes),
 * so that one is recognised by its message. Unlock no longer opens a fee reserve seed on the main thread (docs/18
 * section 3: the worker derives the fee key); a MasterStorageError is still recognised by its code for a main-thread
 * open of a legacy envelope. A message this does not recognise counts as not the passphrase: the door then names the
 * error rather than blame the passphrase.
 */
export function isPassphraseRefusal(e: unknown): boolean {
  if (e instanceof FeeKeyUnsupported || e instanceof WrongDeployment || e instanceof SetupChanged) return false;
  if (e instanceof MasterStorageError) return e.code === 'BadPassphraseOrTampered';
  if (e instanceof WorkerClientError) return /^decrypt(ion failed: wrong passphrase|ed envelope has the wrong length)/.test(e.message);
  return false;
}

class Session {
  private snapshot: SessionSnapshot = { state: 'starting', settings: DEFAULT_SETTINGS, autoLockAt: null, info: null, development: false, forget: null, notice: null };
  private listeners = new Set<Listener>();
  private worker: Worker | null = null;
  private client: WorkerClient | null = null;
  private backendValue: WorkerBackend | null = null;
  private engineValue: Engine | null = null;
  /** The Jupiter API key opened at unlock (5.11), or null when none is configured; cleared at lock like every other in-memory secret. */
  private jupiterApiKeyValue: string | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  /** The runtime teardown in flight (engine close, worker wipe, terminate); a later one waits for an earlier one. Null when none runs. */
  private teardown: Promise<void> | null = null;
  /** Every `lock()` run in flight, its storage re-read included; `settled()` waits for all of them. */
  private locks = new Set<Promise<void>>();
  /**
   * Bumped by every lock and forget, only through `invalidate`. An unlock or adoption that took an older value has
   * lost to one (I3); a lock whose re-read ends under a newer value leaves the final state to the later one; a legacy
   * send-out that took an older value is no longer live (docs/18 2.4 step 4).
   */
  private generation = 0;
  /** The legacy escape hatch's operation (docs/18 2.4, R85): at most one per tab; freed by the operation itself once its last await has settled. */
  private legacyOp: LegacySendOut | null = null;
  /** Workers the session has adopted and not yet terminated (I4). */
  private owned = new Set<Worker>();
  /** A sweep's outcome waiting behind an unlock's own notice (the snapshot has one slot); published when that one is dismissed or a lock clears it. */
  private heldSweep: SweepOutcomeNotice | null = null;
  /**
   * The one-sweep latch (docs/18 section 6; the header above): a sent sweep with no known outcome, given to every
   * engine through `EngineDeps.pendingSweep` and `setPendingSweep`. Survives lock and unlock; Forget clears it.
   */
  private pendingSweep: PendingSweep | null = null;

  /** Read storage once; decides /welcome versus /locked, or Welcome's newer-build sentence. */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    const reads = fencedReads();
    let [sealed, settings, info] = await Promise.all([reads.read(loadSealedMaster(), undefined), reads.read(loadSettings(), DEFAULT_SETTINGS), reads.read(loadVaultInfo(), undefined)]);
    // Development builds only: `?rpc=<url>` points the app at a local cluster
    // (the rpc shim, a test validator) and persists it as the RPC setting, so
    // the Playwright flows run against real program execution. Compiled away
    // in production, where the RPC is chosen in Settings and bounded by the CSP.
    if (import.meta.env.DEV && typeof location !== 'undefined') {
      const q = new URLSearchParams(location.search);
      const rpc = q.get('rpc');
      const jup = q.get('jup');
      if ((rpc && rpc !== settings.rpcUrl) || (jup && jup !== settings.jupiterUrl)) {
        settings = { ...settings, rpcUrl: rpc ?? settings.rpcUrl, jupiterUrl: jup ?? settings.jupiterUrl };
        await saveSettings(settings).catch(() => {});
      }
    }
    // Devices that stored an old default keep hitting an endpoint that refuses browsers (LEGACY_DEFAULT_RPC) or refuses
    // token and stake listing (PUBLICNODE_RPC, once a build sets another default); move them once.
    if (settings.rpcUrl === LEGACY_DEFAULT_RPC || (settings.rpcUrl === PUBLICNODE_RPC && DEFAULT_RPC !== PUBLICNODE_RPC)) {
      settings = { ...settings, rpcUrl: DEFAULT_RPC };
      await saveSettings(settings).catch(() => {});
    }
    // Another tab forgetting this device closes our connection: nothing here can be trusted any more, so lock without
    // re-reading. A later build upgrading it closes it too (writer step 6): the device is then that build's, and a
    // re-read would only fail with a VersionError, or wait behind an upgrade still blocked by another tab.
    onAppDbVersionChange((upgraded) => void this.lock(upgraded ? { newerBuild: true } : { forgotten: true }));
    const state = reads.fenced() ? 'newer-build' : sealed ? 'locked' : 'no-vault';
    this.set({ state, settings, autoLockAt: null, info: info ? displayInfo(info) : null, development: false, forget: null, notice: null });
    this.installActivityHooks();
  }

  get(): SessionSnapshot {
    return this.snapshot;
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  /** An RPC client over the configured endpoint; usable before unlock (Restore reads the chain before any passphrase exists). */
  rpc(): Rpc {
    return new Rpc({ url: this.snapshot.settings.rpcUrl });
  }

  /** The signer's backend while unlocked; throws otherwise so a screen can never sign against a wiped worker. */
  backend(): WorkerBackend {
    if (!this.backendValue || this.snapshot.state !== 'unlocked') throw new Error('locked');
    return this.backendValue;
  }

  /** The engine while unlocked; throws otherwise. Safe during render under RequireUnlocked (I1). */
  engine(): Engine {
    if (!this.engineValue || this.snapshot.state !== 'unlocked') throw new Error('locked');
    return this.engineValue;
  }

  /** The Jupiter API key opened at unlock, or null when this device holds none or the session is locked (5.11). Never read from storage directly: Settings only ever writes the sealed envelope. */
  jupiterApiKey(): string | null {
    return this.jupiterApiKeyValue;
  }

  /** Section 7: switch the program-hash check off or on for this session only (R42). The frame shows a standing warning while it is off. */
  setDevelopment(on: boolean): void {
    this.set({ ...this.snapshot, development: on });
  }

  /** Home or the door has shown the one-session notice (docs/18 sections 3 and 6): it is not shown again in this session. A sweep's outcome held behind it is shown next. */
  dismissNotice(): void {
    const next = this.heldSweep;
    this.heldSweep = null;
    if (this.snapshot.notice || next) this.set({ ...this.snapshot, notice: next });
  }

  /** What a lock or an adoption keeps of the notice: a sweep's outcome, shown or held, and nothing else. Takes the held one. */
  private keptSweep(): SweepOutcomeNotice | null {
    const shown = this.snapshot.notice;
    const kept = shown?.kind === 'sweep-outcome' ? shown : this.heldSweep;
    this.heldSweep = null;
    return kept;
  }

  /**
   * The engine's sweep outcome (docs/18 section 6, "After the sweep"; EngineDeps.onSweepOutcome). Every reported
   * outcome becomes the notice first, for Home, and for the door when a lock came first; behind an unlock's own notice
   * it waits its turn. The page that pressed Move takes it back if it is still mounted to show it (`takeSweepOutcome`),
   * so a page left during the confirmation does not lose it. `afterLock` records whether the session had left the
   * runtime that sent it (`gen` stale, or no longer unlocked). A forget, or a device another tab emptied or a later
   * build upgraded, keeps nothing.
   */
  private sweepOutcome(outcome: SweepOutcome, gen: number): void {
    if (this.snapshot.forget !== null || this.snapshot.state === 'no-vault' || this.snapshot.state === 'newer-build') return;
    const afterLock = gen !== this.generation || this.snapshot.state !== 'unlocked';
    const notice: SweepOutcomeNotice = { kind: 'sweep-outcome', signature: outcome.signature, amount: outcome.amount, form: outcome.form, landed: outcome.landed, failed: outcome.failed, detail: outcome.detail, afterLock };
    if (this.snapshot.notice && this.snapshot.notice.kind !== 'sweep-outcome') this.heldSweep = notice;
    else this.set({ ...this.snapshot, notice });
  }

  /**
   * The page that pressed Move is still mounted and shows the outcome of `signature` itself (OlderReserveNotice): the
   * notice kept for it goes, shown or held, so Home does not show it a second time. Any other notice stays.
   */
  takeSweepOutcome(signature: string): void {
    if (this.heldSweep?.signature === signature) {
      this.heldSweep = null;
      return;
    }
    const shown = this.snapshot.notice;
    if (shown?.kind === 'sweep-outcome' && shown.signature === signature) this.set({ ...this.snapshot, notice: null });
  }

  /**
   * Open the sealed master inside the worker and re-derive the vault's fee
   * reserve there (docs/18-one-fee-reserve.md section 3; the steps below
   * are its numbers). The passphrase goes to the worker and nowhere else; a
   * wrong one rejects and leaves the state locked. Nothing about the vault
   * is read from the network here: screens sync with the chain themselves,
   * and the network check of 2.5 runs at the first fee use, so unlocking
   * costs no network request. Waits for any lock or teardown in flight
   * first (I2); a lock or forget that starts meanwhile wins (I3): the token
   * is checked after every await, and a lost unlock wipes and ends its
   * worker and rejects with "locked". A worker this opened is wiped and
   * ended on every failure.
   */
  async unlock(passphrase: string): Promise<void> {
    // docs/18 2.4 step 3: an unlock started in this tab ends a legacy send-out first (`adopt` does again before installing).
    this.legacyOp?.cancel('unlock');
    // Step 1. Reading storage now would wait behind a queued deletion and then find it empty (R60).
    if (this.snapshot.forget) throw new Error(FORGETTING);
    // Unlocking over a live session locks it first: one runtime (I2). Defensive: /locked and Welcome redirect an unlocked session to /vault.
    if (this.snapshot.state === 'unlocked') await this.lock();
    // A runtime still being torn down, or a lock still re-reading storage, finishes first (I2, R68).
    await this.settled();
    if (this.snapshot.forget) throw new Error(FORGETTING);
    // A lock or forget from here on wins over this unlock (I3). The session's own token decides, never whether an engine happens to be live.
    const gen = this.generation;
    const lost = () => gen !== this.generation || this.snapshot.forget !== null;
    // Step 2: both are required; the legacy relayer envelope is not read at unlock.
    const [sealed, stored] = await Promise.all([loadSealedMaster(), loadVaultInfo()]);
    if (lost()) throw new Error('locked');
    if (!sealed || !stored) throw new Error('this device holds no complete vault setup');
    const { worker, client } = this.freshWorker();
    let opened: OpenedSetup;
    try {
      opened = await this.openSetup(client, sealed, stored, passphrase, lost);
    } catch (e) {
      await endWorker(client, worker);
      throw e;
    }
    // Step 6. Another unlock in flight may have installed the runtime first (I2): this one's worker is then ended and the key left to that one.
    if (!(await this.adopt(client, worker, opened.relayer, opened.info, gen, opened.notice, sealed, opened.rowFeeWallet))) return;
    // The Jupiter key, when this device has one sealed, opens under the same
    // passphrase that just unsealed the master (Settings/5.11 seals it that
    // way). A device with no key configured leaves this null. Opening it is
    // not load-bearing for the vault itself, so a failure here does not fail
    // the unlock; Settings lets the key be replaced. A lock during this await
    // keeps it closed: only the session that opened it keeps it.
    const sealedKey = this.snapshot.settings.jupiterApiKeySealed;
    const key = sealedKey ? await openJupiterKey(sealedKey, passphrase).catch(() => null) : null;
    if (gen === this.generation && this.snapshot.state === 'unlocked') this.jupiterApiKeyValue = key;
  }

  /**
   * Unlock steps 3 to 5 (docs/18 section 3) in `client`'s fresh worker; the caller wipes and ends that worker when
   * this rejects. Every await is followed by the I3 check (`lost`), so a stale worker response is dropped by the
   * session's own generation. Resolves to the worker-backed fee signer (step 6), the row to adopt and the notice.
   */
  private async openSetup(client: WorkerClient, sealed: SealedMaster, stored: VaultInfo, passphrase: string, lost: () => boolean): Promise<OpenedSetup> {
    const live = (): void => {
      if (lost()) throw new Error('locked');
    };
    // Every await below settles through this: the I3 token is checked first whether the step resolved or rejected,
    // so a lost unlock rejects with "locked" and never with what a stale worker or store answered (a wrong
    // passphrase from `unseal`, a failure from `activateFeePayer`).
    const checked = async <T>(step: Promise<T>): Promise<T> => {
      let value: T;
      try {
        value = await step;
      } catch (e) {
        live();
        throw e;
      }
      live();
      return value;
    };
    // Step 3, the capability probe first (2.4, "Browser support"): a browser that cannot hold the fee key is refused
    // before the passphrase is used, and nothing in storage changes.
    try {
      await checked(client.feeProbe());
    } catch (e) {
      if (e instanceof WorkerClientError && e.code === 'FeeKeyUnsupported') throw new FeeKeyUnsupported(e.message);
      throw e;
    }
    await checked(client.unseal(sealed, passphrase)); // rejects on a wrong passphrase or a tampered envelope
    // Step 4, the deployment check: VaultInfo stores no program id, and builds of two deployments can share one
    // localhost origin, so the stored address is recomputed under this build's program id. It catches a different
    // program id, with no network read; a different network is 2.5's check, at the first fee use.
    const expected = await checked(vaultAddress(address(PROGRAM_ID_B58), bytesOf(stored.genesisKey)));
    if (expected.address !== stored.address) throw new WrongDeployment();
    // Step 5: the worker derives the fee reserve (n = 0) and installs it as the active payer; the key stays there.
    const derived = await checked(client.activateFeePayer(0));
    const feeWallet = toBase58(derived);
    let info = stored;
    // The row's `feeWallet` after this step, for the writer of a pre-R48 address (`legacyAddressWriter`).
    let rowFeeWallet: unknown = stored.feeWallet;
    let notice: SessionNotice | null = null;
    if (stored.feeWallet !== feeWallet) {
      // Absent (a device set up before this release) or different (out of date, or tampered): the derived address
      // is used and stored, through the compare-and-set writer (R84). Writer step 2: the I3 check immediately before
      // the call, with no await between them (`checked` itself resumes a microtask after its own check). A writer
      // that fails (the database closed by another tab's forget, or a VersionError from a later build's fence, writer
      // step 6) writes nothing; a lost unlock still reports "locked".
      live();
      const res = await checked(updateFeeFields({ sealedMaster: sealed, vaultId: stored.vaultId, address: stored.address, feeWallet: stored.feeWallet }, { feeWallet }));
      if (res.current && (res.written || (res.sameSetup && res.current.feeWallet === feeWallet))) {
        // Writer step 4: adopt after a write, or when the same setup's row already holds the derived address (another
        // tab of this setup wrote it first).
        info = res.current;
        rowFeeWallet = info.feeWallet;
      } else if (res.current && res.sameSetup && res.comparison === 'incomparable') {
        // The same setup, whose stored value is of a kind the writer cannot compare (R92: a Blob, a File, a kind this
        // build does not read): it is left as it is, never written over, and the derived key is used as in every
        // case, since the stored value is display data only. Home says so once; the row keeps that value for any
        // later write of this runtime (a pre-R48 address, which the writer then skips the same way).
        info = { ...res.current, feeWallet };
        notice = { kind: 'fee-wallet-unreadable' };
      } else {
        // Anything else (another setup, a row changed to another address) abandons.
        throw new SetupChanged();
      }
      // The notice names the old address; a stored value that is not a string (rewritten too, compared by value)
      // names none, so it is replaced without one.
      if (typeof stored.feeWallet === 'string') notice = { kind: 'fee-wallet-rewritten', previous: stored.feeWallet };
    }
    // Step 6's signer: `sign` is the worker's feeSign, so the engine signs unchanged and no seed is on this thread.
    return { relayer: workerFeeSigner(client, derived), info, notice, rowFeeWallet };
  }

  /**
   * After Create or Restore has sealed and stored everything: the worker holds the master already, so no passphrase
   * round-trip. Create and Restore pass `workerFeeSigner(client, await client.activateFeePayer(0))` as `relayer`, and
   * an `info` whose `feeWallet` is that key's address (docs/18 section 4). Rejects with "locked" if a lock or forget
   * starts meanwhile (I3), after wiping and ending the worker.
   */
  async adoptUnlocked(client: WorkerClient, worker: Worker, relayer: RelayerSigner, info: VaultInfo): Promise<void> {
    // Defensive (Create and Restore run only over an empty device, App.tsx RequireNoVault): never two runtimes.
    if (this.snapshot.state === 'unlocked') await this.lock();
    await this.adopt(client, worker, relayer, info, this.generation);
  }

  /**
   * Install a runtime whose worker holds the master, unless a lock or forget started after `gen` was taken (I3).
   * Resolves true when this call installed it. Resolves false, after wiping and ending the newcomer's worker, when
   * another adoption installed a runtime meanwhile (I2): two unlocks in flight, the door remounted by Back or
   * Forward while the first was opening. The session is then unlocked over this device's one stored setup, so the
   * later caller's request is met; replacing the runtime instead would leave the earlier worker, master and trees
   * alive with nothing to end them. Any other failure (the ledger refusing to open) also ends the worker.
   */
  private async adopt(client: WorkerClient, worker: Worker, relayer: RelayerSigner, info: VaultInfo, gen: number, notice: SessionNotice | null = null, sealed: SealedMaster | null = null, rowFeeWallet: unknown = info.feeWallet): Promise<boolean> {
    const end = (): Promise<void> => endWorker(client, worker);
    // Taking the token does not cancel a lock already running: its caller may read the state it settles to
    // (savedSetup.ts markSetupSavedAfter, Ceremony.tsx reopenSaved). Waiting here orders it first (I2).
    await this.settled();
    const lost = () => gen !== this.generation || this.snapshot.forget !== null;
    const abandon = async (): Promise<never> => {
      await end();
      throw new Error('locked');
    };
    // Checked before the ledger is opened: an open queued behind a forget's deletion would re-create an empty ledger.
    if (lost()) return abandon();
    // A runtime installed by another adoption since the caller's entry check (I2). Not lost, so the snapshot says 'unlocked' over it.
    if (this.engineValue) {
      await end();
      return false;
    }
    // The ledger closes itself when another tab deletes it (R47); this tab then locks rather than run on a closed store.
    let store: IndexedDbLedgerStore;
    try {
      store = await IndexedDbLedgerStore.open(undefined, undefined, { onVersionChange: () => void this.lock({ forgotten: true }) });
    } catch (e) {
      await end();
      throw e;
    }
    if (lost()) {
      store.close();
      return abandon();
    }
    // The same check after the open's await; from here to the install nothing awaits, so no other adoption interleaves.
    if (this.engineValue) {
      store.close();
      await end();
      return false;
    }
    // docs/18 2.4 step 3: an unlock already in flight when a legacy send-out began ends it here, before a runtime exists.
    this.legacyOp?.cancel('unlock');
    const backend = new WorkerBackend(client, PROGRAM_ID);
    this.worker = worker;
    this.client = client;
    this.backendValue = backend;
    this.engineValue = new Engine({
      rpc: this.rpc(),
      store,
      backend,
      relayer,
      programId: PROGRAM_ID,
      info,
      settings: () => this.snapshot.settings,
      jupiterApiKey: () => this.jupiterApiKeyValue,
      development: () => this.snapshot.development,
      client,
      // Read on demand, never at unlock (docs/18 section 3).
      legacyEnvelope: () => loadSealedRelayer(),
      recordLegacyAddress: this.legacyAddressWriter(sealed, info, gen, rowFeeWallet),
      // docs/18 section 6, "After the sweep": kept as the notice until the page that pressed Move takes it.
      onSweepOutcome: (outcome) => this.sweepOutcome(outcome, gen),
      // The one-sweep latch is the session's, so it outlives this runtime (the header above).
      pendingSweep: () => this.pendingSweep,
      setPendingSweep: (sweep) => {
        this.pendingSweep = sweep;
      },
    });
    this.owned.add(worker);
    // The notice is published with 'unlocked' (null from Create and Restore), replacing any earlier session's, except
    // a sweep's outcome from an earlier runtime: it is shown now, or after this unlock's own notice.
    const sweep = this.keptSweep();
    if (notice && sweep) this.heldSweep = sweep;
    this.set({ ...this.snapshot, state: 'unlocked', info, notice: notice ?? sweep });
    this.touch();
    return true;
  }

  /**
   * The writer the engine records a pre-R48 device's older address through (docs/18 section 6, "Opening the legacy key,
   * on demand"): `updateFeeFields` with `{ relayer }` over the identity this unlock captured, the sealed master it
   * unsealed, the row it adopted and `rowFeeWallet`, the stored fee value the compare-and-set expects (section 3,
   * writer steps 1 and 3: the derived address once unlock wrote it, or a value the writer cannot compare, left as read).
   * The I3 check comes immediately before the call (writer step 2). Resolves once the address is stored, by this call
   * or already; resolves without a write over a row whose fee value the writer cannot compare (R92: nothing is ever
   * written over it, and the engine keeps the opened address for this runtime); rejects `SetupChanged` when another
   * setup, fee address or older address is stored, and "locked" once a lock or forget has started since this runtime
   * was adopted. Create and Restore capture no sealed master; a device they set up holds no legacy envelope, so their
   * writer refuses.
   */
  private legacyAddressWriter(sealed: SealedMaster | null, info: VaultInfo, gen: number, rowFeeWallet: unknown): (relayer: string) => Promise<void> {
    return async (relayer) => {
      const lost = () => gen !== this.generation || this.snapshot.forget !== null;
      if (!sealed) throw new SetupChanged();
      if (lost()) throw new Error('locked');
      const res = await updateFeeFields({ sealedMaster: sealed, vaultId: info.vaultId, address: info.address, feeWallet: rowFeeWallet }, { relayer });
      if (res.current?.relayer === relayer && (res.written || res.sameSetup)) {
        // The row now names the older address; the snapshot follows while this runtime still owns it (the Forget sheet reads it).
        if (!lost() && this.snapshot.state === 'unlocked') this.set({ ...this.snapshot, info: displayInfo(res.current) });
        return;
      }
      if (res.current && res.sameSetup && res.comparison === 'incomparable' && res.current.relayer === undefined) return;
      throw new SetupChanged();
    };
  }

  /**
   * A fresh worker for unlock and for Create/Restore flows to generate, adopt and seal in; handed back through
   * `adoptUnlocked`, or ended by the flow (`discardWorker`) if abandoned. Development builds only: while the page sets
   * `window.__thevault_breakFeeKey = true`, the worker is named 'thevault-test-no-ed25519', which makes its Ed25519
   * key imports fail (worker-dev-seam.ts), so the specs can drive the capability probe's refusal (docs/18 2.4). Each
   * branch spells out its own static options, which Vite needs to bundle the worker; `import.meta.env.DEV` is false in
   * production, so only the plain construction ships.
   */
  freshWorker(): { worker: Worker; client: WorkerClient } {
    const breakFeeKey = import.meta.env.DEV && typeof window !== 'undefined' && (window as unknown as { __thevault_breakFeeKey?: unknown }).__thevault_breakFeeKey === true;
    const worker = breakFeeKey
      ? new Worker(new URL('./worker-entry.ts', import.meta.url), { type: 'module', name: 'thevault-test-no-ed25519' })
      : new Worker(new URL('./worker-entry.ts', import.meta.url), { type: 'module' });
    return { worker, client: new WorkerClient(worker) };
  }

  /**
   * Create and Restore end their copy of the keys with this rather than `worker.terminate()` once they have handed the
   * worker to `adoptUnlocked` (R67's saved screen; R68): a worker the session adopted is ended by the session's own
   * teardown only, after its wipe (I4). Terminating it under that teardown would leave the wipe unanswered
   * (WorkerClient has no timeout), and every later lock and unlock would wait on it. A worker the session never
   * adopted (a failed adoption) is ended at once.
   */
  discardWorker(worker: Worker): void {
    if (!this.owned.has(worker)) worker.terminate();
  }

  /**
   * Lock. Leaving 'unlocked' is published first (R68), so no screen renders
   * an unlocked session without its engine; then the runtime is dropped and
   * torn down (`dropRuntime`) and storage is re-read. Resolves once that
   * teardown, and any already in flight, has ended and the re-read has
   * settled. Idempotent. The development switch goes with it (R42). With
   * `forgotten`, storage is not re-read: another tab deleted it, and the
   * device is empty from this tab's point of view too. With `newerBuild`,
   * not re-read either: a later build is upgrading the database (the
   * fence, docs/18 section 3, writer step 6), and the state says so. A
   * re-read that meets the fence's VersionError says so too, never
   * 'no-vault'. A lock or forget started after this one owns the final
   * state: this one's re-read then sets nothing. From a state other than
   * 'unlocked' (the saved screen's re-read, R67) nothing is published
   * before the re-read, as before.
   */
  lock(opts: LockOptions = {}): Promise<void> {
    const run = this.runLock(opts);
    this.locks.add(run);
    const done = () => {
      this.locks.delete(run);
    };
    run.then(done, done);
    return run;
  }

  private async runLock(opts: LockOptions): Promise<void> {
    this.clearTimers();
    // The reason decides whether a legacy send-out's confirmation may finish (docs/18 2.4 step 3): another tab's Forget
    // or a later build's upgrade arrives as a version change, and the lock this tab's Forget runs first counts as a
    // Forget (its pending status is published before the lock starts); both abort in every phase.
    const reason: LegacyCancelReason = opts.forgotten || opts.newerBuild ? 'versionchange' : this.snapshot.forget ? 'forget' : opts.auto ? 'auto-lock' : 'lock';
    const gen = this.invalidate(reason);
    if (opts.forgotten || opts.newerBuild) {
      // The device is no longer this build's to open: no notice, a sweep's outcome included, has a screen to show it,
      // and no later runtime of this setup can resume a sweep's watch.
      this.heldSweep = null;
      this.pendingSweep = null;
      this.set({ ...this.snapshot, state: opts.newerBuild ? 'newer-build' : 'no-vault', autoLockAt: null, info: null, development: false, notice: null });
      await this.dropRuntime();
      return;
    }
    // Leaving 'unlocked' is said before the runtime goes (I1). The vault's public info stays, for the door and the chip;
    // the one-session notice goes, except a sweep's outcome (docs/18 section 6), which the door shows.
    if (this.snapshot.state === 'unlocked') this.set({ ...this.snapshot, state: 'locked', autoLockAt: null, development: false, notice: this.keptSweep() });
    await this.dropRuntime();
    const reads = fencedReads();
    const [sealed, info] = await Promise.all([reads.read(loadSealedMaster(), undefined), reads.read(loadVaultInfo(), undefined)]);
    if (gen !== this.generation) return;
    const state = reads.fenced() ? 'newer-build' : sealed ? 'locked' : 'no-vault';
    // A sweep's outcome the engine reported during the teardown is kept with the door, never with an empty device.
    const kept = this.keptSweep();
    this.set({ ...this.snapshot, state, autoLockAt: null, info: info ? displayInfo(info) : null, development: false, notice: state === 'locked' ? kept : null });
  }

  /**
   * The only place `generation` changes (docs/18 2.4, "The escape hatch's lifetime", step 3): a lock, a forget's retry
   * and `forgotten` take a new token here, and a legacy send-out, if one exists, is cancelled synchronously, before the
   * caller's next await. Returns the new token.
   */
  private invalidate(reason: LegacyCancelReason): number {
    const gen = ++this.generation;
    this.legacyOp?.cancel(reason);
    // The timers armed for that operation end with it (2.4 step 3, auto-lock); an unlocked session's own are cleared by its lock.
    if (this.snapshot.state !== 'unlocked') this.clearTimers();
    return gen;
  }

  /**
   * Begin the legacy escape hatch's operation (docs/18 2.4, "The escape hatch's lifetime", steps 1 and 2) and claim the
   * slot synchronously, before any await, so a second call (a double press) is refused. Refused while a forget is
   * pending or blocked, while unlocked, outside the locked door, and while the slot is occupied, including by an
   * operation whose sent transaction is still being confirmed. The operation's entry (`open`) waits for `settled()`,
   * checks the forget state again and captures the token and the setup identity; its own `finally` frees the slot.
   * While it is live and the session is 'locked' the idle and hidden-tab timers run as they do while unlocked; its
   * cancel stops them (`legacyStopped`).
   */
  beginLegacySendOut(): LegacySendOutHandle {
    if (this.snapshot.forget) throw new Error(FORGETTING);
    if (this.snapshot.state === 'unlocked') throw new Error('The app is unlocked: the older fee reserve is moved from Home.');
    if (this.legacyOp) throw new Error('A send-out of the older fee reserve is already running in this tab. Wait for it to end.');
    if (this.snapshot.state !== 'locked') throw new Error('This device holds no vault setup to send from.');
    const op = new LegacySendOut({
      generation: () => this.generation,
      forgetting: () => this.snapshot.forget !== null,
      settled: () => this.settled(),
      rpcUrl: this.snapshot.settings.rpcUrl,
      released: (ended) => this.legacyReleased(ended),
      stopped: (cancelled) => this.legacyStopped(cancelled),
    });
    this.legacyOp = op;
    this.touch();
    return op.handle;
  }

  /** Step 6: the operation has ended and its last await has settled. The slot is freed, with the timers armed for it (an unlocked session keeps its own). */
  private legacyReleased(op: LegacySendOut): void {
    if (this.legacyOp !== op) return;
    this.legacyOp = null;
    if (this.snapshot.state !== 'unlocked') this.clearTimers();
  }

  /**
   * Step 3: the operation was cancelled (a dismissal, an unlock, `pagehide`, or `invalidate`). Its timers stop now: a
   * confirmation after the latch keeps the slot until it settles, and a timer left armed meanwhile would lock, taking a
   * new token, over an unlock started in that time (I3).
   */
  private legacyStopped(op: LegacySendOut): void {
    if (this.legacyOp === op && this.snapshot.state !== 'unlocked') this.clearTimers();
  }

  /** docs/18 2.4 step 3, auto-lock: the timers run for a legacy send-out only while it is live and the door is up. */
  private legacyArmed(): boolean {
    return this.legacyOp !== null && this.legacyOp.live() && this.snapshot.state === 'locked';
  }

  /**
   * DEV-only read-out for the escape hatch's specs (docs/18 section 10; main.tsx `__thevault_test.legacySendOut`): the
   * slot's operation, its phase, whether it holds a signer, wire, envelope or seed and how many times a signer was
   * installed, never their contents. Null when the slot is empty, and always in a production build.
   */
  legacySendOut(): ReturnType<LegacySendOut['readout']> | null {
    if (!import.meta.env.DEV) return null;
    return this.legacyOp?.readout() ?? null;
  }

  /** Every lock run and runtime teardown in flight has ended. Never rejects. */
  private async settled(): Promise<void> {
    while (this.locks.size > 0 || this.teardown) {
      await Promise.allSettled([...this.locks, this.teardown]);
    }
  }

  /**
   * "Forget this device" (5.9): lock first, so this tab's engine has closed
   * its ledger connection and the worker is gone, then delete both
   * databases (R47). The lock and the pending status are published together
   * (R68, R53's order): the screen up next shows "Forgetting this device."
   * with no unlock form for the whole teardown, and `unlock` refuses
   * meanwhile (R60). The delete starts only once the lock has resolved,
   * which includes any teardown already in flight. A delete blocked by
   * another connection rejects with `ForgetBlocked`; the device is then
   * still locked and the outcome is kept in the snapshot for whichever
   * screen is up after the lock (R53). Calling this again retries with a
   * fresh timeout on the same queued deletion, without re-reading a
   * database that is waiting to be deleted (R59). The queued deletion
   * cannot be withdrawn: when it completes on its own, the session
   * reconciles to `no-vault` (R60). Resolves to true when both databases
   * are gone.
   */
  async forget(): Promise<boolean> {
    if (this.snapshot.forget?.status === 'blocked') {
      // A retry. `lock()` would open the app database to re-read the sealed master, and that open waits
      // behind the deletion already queued, so the retry would never reach its own timeout (R59).
      this.invalidate('forget');
      await this.dropRuntime();
      this.heldSweep = null;
      this.pendingSweep = null;
      this.set({ ...this.snapshot, forget: { status: 'pending' }, notice: null });
    } else {
      // Forget clears every notice, a sweep's outcome included, and the one-sweep latch (docs/18 section 6).
      const state = this.snapshot.state === 'unlocked' ? 'locked' : this.snapshot.state;
      this.heldSweep = null;
      this.pendingSweep = null;
      this.set({ ...this.snapshot, state, autoLockAt: null, development: false, forget: { status: 'pending' }, notice: null });
      await this.lock();
    }
    try {
      await forgetDevice();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      this.set({ ...this.snapshot, forget: { status: 'blocked', message } });
      this.reconcileForget();
      return false;
    }
    this.forgotten();
    return true;
  }

  /** The device is empty: both databases are gone. No runtime survives it (none is expected: forget locked first, and unlock refuses meanwhile). */
  private forgotten(): void {
    this.invalidate('forget');
    this.clearTimers();
    this.heldSweep = null;
    this.pendingSweep = null;
    this.set({ ...this.snapshot, state: 'no-vault', autoLockAt: null, info: null, development: false, forget: null, notice: null });
    void this.dropRuntime();
  }

  private watchedForget: Promise<void> | null = null;

  /**
   * A blocked forget's deletion is still queued and completes the moment
   * the other connection closes, whatever this tab shows meanwhile. When it
   * does, the device is empty and the session says so rather than leaving a
   * locked screen over storage that no longer exists (R60).
   */
  private reconcileForget(): void {
    const done = outstandingForget();
    if (!done || this.watchedForget === done) return;
    this.watchedForget = done;
    done.then(
      () => {
        if (this.snapshot.forget?.status === 'blocked') this.forgotten();
      },
      () => {
        /* the failure was reported to the attempt that awaited it */
      },
    );
  }

  /**
   * Invalidate the runtime and start its teardown. Call only after publishing a state other than 'unlocked' (I1).
   * The engine's close starts synchronously, so its signer refuses every later call from this instant (R18/R21); it
   * waits for a running signer job (a worker sign or a ledger write), wipes the worker's trees and closes the ledger
   * store. Then the worker's master is wiped and the worker terminated. Resolves when this teardown, and any earlier
   * one, has ended; with nothing to drop it joins the teardown in flight rather than resolving at once, so forget's
   * delete never starts while this tab's ledger connection is still open (R47).
   */
  private dropRuntime(): Promise<void> {
    const engine = this.engineValue;
    const client = this.client;
    const worker = this.worker;
    const violated = import.meta.env.DEV && engine !== null && this.snapshot.state === 'unlocked';
    this.engineValue = null;
    this.client = null;
    this.worker = null;
    this.backendValue = null;
    this.jupiterApiKeyValue = null;
    if (engine || client || worker) {
      const closed = engine ? engine.close().catch(() => {}) : Promise.resolve();
      const previous = this.teardown;
      const run = (async () => {
        await closed;
        if (previous) await previous.catch(() => {});
        if (client) {
          try {
            await client.wipe();
          } catch {
            /* a wedged worker is terminated below regardless */
          }
        }
        if (worker) {
          worker.terminate();
          this.owned.delete(worker);
        }
      })();
      this.teardown = run;
      const done = () => {
        if (this.teardown === run) this.teardown = null;
      };
      run.then(done, done);
    }
    if (violated) {
      // Reported once the teardown has started, so a violation never leaves the master in a live worker.
      queueMicrotask(() => {
        throw new Error('session invariant (R68): the runtime was dropped while the session still said unlocked');
      });
    }
    return this.teardown ?? Promise.resolve();
  }

  async reloadSettings(): Promise<void> {
    const settings = await loadSettings().catch(() => DEFAULT_SETTINGS);
    this.set({ ...this.snapshot, settings });
    if (!settings.jupiterApiKeySealed) this.jupiterApiKeyValue = null;
    if (this.snapshot.state === 'unlocked') this.touch();
  }

  /** Settings just sealed (or cleared) the Jupiter key: make the running session use it now rather than at the next unlock. */
  adoptJupiterApiKey(key: string | null): void {
    this.jupiterApiKeyValue = this.snapshot.state === 'unlocked' ? key : null;
  }

  /**
   * Any user interaction restarts the idle clock (Settings: 1 to 60 minutes). Inert from the moment a lock is published,
   * except while a legacy send-out is live on the door (docs/18 2.4 step 3, auto-lock): its lock cancels the operation.
   * Only an unlocked session publishes `autoLockAt`.
   */
  touch(): void {
    const unlocked = this.snapshot.state === 'unlocked';
    if (!unlocked && !this.legacyArmed()) {
      // Nothing to time: a timer still armed for an operation that has stopped goes too.
      this.clearTimers();
      return;
    }
    if (this.idleTimer) clearTimeout(this.idleTimer);
    const ms = Math.min(60, Math.max(1, this.snapshot.settings.autoLockMinutes)) * 60_000;
    this.idleTimer = setTimeout(() => void this.lock({ auto: true }), ms);
    if (unlocked) this.set({ ...this.snapshot, autoLockAt: Date.now() + ms });
  }

  private installActivityHooks(): void {
    if (typeof document === 'undefined') return;
    for (const ev of ['pointerdown', 'keydown'] as const) document.addEventListener(ev, () => this.touch(), { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        if (this.snapshot.state === 'unlocked' || this.legacyArmed()) {
          if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
          this.hiddenTimer = setTimeout(() => void this.lock({ auto: true }), 60_000);
        }
      } else if (this.hiddenTimer) {
        clearTimeout(this.hiddenTimer);
        this.hiddenTimer = null;
      }
    });
    // docs/18 2.4 step 3, tab close: synchronous, whether or not the page enters the back-forward cache, so a page
    // restored from that cache never resumes a legacy send-out and the door shows no operation.
    window.addEventListener('pagehide', () => this.legacyOp?.cancel('pagehide'));
  }

  private clearTimers(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
    this.idleTimer = null;
    this.hiddenTimer = null;
  }

  private set(next: SessionSnapshot): void {
    // I1, checked in development builds: an unlocked session always has its engine. Refused, never published.
    if (import.meta.env.DEV && next.state === 'unlocked' && !this.engineValue) throw new Error('session invariant (R68): unlocked without an engine');
    this.snapshot = next;
    for (const l of this.listeners) l();
  }
}

export const session = new Session();

/** React binding: re-renders on every session change. */
export function useSession(): SessionSnapshot {
  return useSyncExternalStore(
    (l) => session.subscribe(l),
    () => session.get(),
    () => session.get(),
  );
}
